/**
 * Firestore Helper Functions for Multi-Unit Projects (Phase 9)
 *
 * These functions manage the projects, segments, and units collections.
 * Projects are stored in: /contractors/{contractorId}/projects/{projectId}
 * Segments are stored in: /contractors/{contractorId}/projects/{projectId}/segments/{segmentId}
 * Units are stored in: /contractors/{contractorId}/projects/{projectId}/units/{unitId}
 *
 * Usage:
 *   import { createProject, addUnitsToProject } from '@/lib/firestore-project-helpers';
 *
 *   // Create a project
 *   const projectId = await createProject(contractorId, projectData);
 *
 *   // Add units to a project
 *   await addUnitsToProject(contractorId, projectId, units);
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint,
  DocumentData,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  DevelopmentProject,
  CreateDevelopmentProjectInput,
  UpdateDevelopmentProjectInput,
  ProjectSegment,
  Unit,
  CreateUnitInput,
  UnitStats,
  SharedPermitStats,
  JobScopeType,
  DevelopmentProjectStatus,
} from '@/types/project';

// ============================================
// PROJECTS COLLECTION
// ============================================

/**
 * Create a new development project
 */
export async function createProject(
  contractorId: string,
  createdBy: string,
  input: CreateDevelopmentProjectInput
): Promise<string> {
  const projectRef = doc(collection(db, `contractors/${contractorId}/projects`));
  const now = Timestamp.now();

  const project: Omit<DevelopmentProject, 'id'> = {
    name: input.name,
    developmentType: input.developmentType,
    propertyType: input.propertyType,
    description: input.description,
    masterAddress: input.masterAddress,
    jurisdictionId: input.jurisdictionId,
    ownerName: input.ownerName,
    ownerCompany: input.ownerCompany,
    ownerPhone: input.ownerPhone,
    ownerEmail: input.ownerEmail,
    totalUnits: input.totalUnits,
    segments: [],
    status: 'planning',
    unitStats: {
      total: 0,
      ready: 0,
      blocked: 0,
      submitted: 0,
      issued: 0,
    },
    sharedPermitStats: {
      total: 0,
      ready: 0,
      blocked: 0,
      submitted: 0,
      issued: 0,
    },
    contractorId,
    createdBy,
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(projectRef, project);
  return projectRef.id;
}

/**
 * Update a development project
 */
export async function updateProject(
  contractorId: string,
  projectId: string,
  updates: UpdateDevelopmentProjectInput
): Promise<void> {
  const projectRef = doc(db, `contractors/${contractorId}/projects/${projectId}`);
  await setDoc(projectRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Get a single project
 */
export async function getProject(
  contractorId: string,
  projectId: string
) {
  const projectRef = doc(db, `contractors/${contractorId}/projects/${projectId}`);
  return getDoc(projectRef);
}

/**
 * Get all projects for a contractor
 */
export async function getProjects(
  contractorId: string,
  constraints: QueryConstraint[] = []
) {
  const q = query(
    collection(db, `contractors/${contractorId}/projects`),
    ...constraints
  );
  return getDocs(q);
}

/**
 * Soft delete a project
 */
export async function softDeleteProject(
  contractorId: string,
  projectId: string,
  deletedBy: string
): Promise<void> {
  const projectRef = doc(db, `contractors/${contractorId}/projects/${projectId}`);
  await setDoc(projectRef, {
    isDeleted: true,
    deletedAt: Timestamp.now(),
    deletedBy,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Update project stats (unit counts)
 */
export async function updateProjectStats(
  contractorId: string,
  projectId: string,
  unitStats?: UnitStats,
  sharedPermitStats?: SharedPermitStats
): Promise<void> {
  const projectRef = doc(db, `contractors/${contractorId}/projects/${projectId}`);
  const updates: DocumentData = {
    updatedAt: Timestamp.now(),
  };
  if (unitStats) updates.unitStats = unitStats;
  if (sharedPermitStats) updates.sharedPermitStats = sharedPermitStats;
  await setDoc(projectRef, updates, { merge: true });
}

// ============================================
// SEGMENTS SUBCOLLECTION
// ============================================

/**
 * Create a segment in a project
 */
export async function createSegment(
  contractorId: string,
  projectId: string,
  segment: Omit<ProjectSegment, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const segmentRef = doc(collection(db, `contractors/${contractorId}/projects/${projectId}/segments`));
  const now = Timestamp.now();

  await setDoc(segmentRef, {
    ...segment,
    projectId,
    createdAt: now,
    updatedAt: now,
  });

  return segmentRef.id;
}

/**
 * Update a segment
 */
export async function updateSegment(
  contractorId: string,
  projectId: string,
  segmentId: string,
  updates: Partial<ProjectSegment>
): Promise<void> {
  const segmentRef = doc(db, `contractors/${contractorId}/projects/${projectId}/segments/${segmentId}`);
  await setDoc(segmentRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Get all segments for a project
 */
export async function getSegments(
  contractorId: string,
  projectId: string,
  constraints: QueryConstraint[] = []
) {
  const q = query(
    collection(db, `contractors/${contractorId}/projects/${projectId}/segments`),
    orderBy('sortOrder', 'asc'),
    ...constraints
  );
  return getDocs(q);
}

/**
 * Delete a segment
 */
export async function deleteSegment(
  contractorId: string,
  projectId: string,
  segmentId: string
): Promise<void> {
  const segmentRef = doc(db, `contractors/${contractorId}/projects/${projectId}/segments/${segmentId}`);
  await deleteDoc(segmentRef);
}

// ============================================
// UNITS SUBCOLLECTION
// ============================================

/**
 * Create a unit in a project
 */
export async function createUnit(
  contractorId: string,
  projectId: string,
  input: CreateUnitInput
): Promise<string> {
  const unitRef = doc(collection(db, `contractors/${contractorId}/projects/${projectId}/units`));
  const now = Timestamp.now();

  const unit: Omit<Unit, 'id'> = {
    projectId,
    segmentId: input.segmentId,
    label: input.label,
    unitType: input.unitType,
    description: input.description,
    address: input.address,
    parcelId: input.parcelId,
    attributes: input.attributes,
    status: 'pending',
    jobCount: 0,
    readyJobs: 0,
    blockedJobs: 0,
    sortOrder: input.sortOrder ?? 0,
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(unitRef, unit);
  return unitRef.id;
}

/**
 * Batch create multiple units (efficient for large projects)
 */
export async function batchCreateUnits(
  contractorId: string,
  projectId: string,
  units: CreateUnitInput[]
): Promise<string[]> {
  const batch = writeBatch(db);
  const now = Timestamp.now();
  const unitIds: string[] = [];

  for (const input of units) {
    const unitRef = doc(collection(db, `contractors/${contractorId}/projects/${projectId}/units`));
    unitIds.push(unitRef.id);

    const unit: Omit<Unit, 'id'> = {
      projectId,
      segmentId: input.segmentId,
      label: input.label,
      unitType: input.unitType,
      description: input.description,
      address: input.address,
      parcelId: input.parcelId,
      attributes: input.attributes,
      status: 'pending',
      jobCount: 0,
      readyJobs: 0,
      blockedJobs: 0,
      sortOrder: input.sortOrder ?? 0,
      createdAt: now,
      updatedAt: now,
    };

    batch.set(unitRef, unit);
  }

  // Update project's unit count
  const projectRef = doc(db, `contractors/${contractorId}/projects/${projectId}`);
  batch.update(projectRef, {
    updatedAt: now,
  });

  await batch.commit();
  return unitIds;
}

/**
 * Update a unit
 */
export async function updateUnit(
  contractorId: string,
  projectId: string,
  unitId: string,
  updates: Partial<Unit>
): Promise<void> {
  const unitRef = doc(db, `contractors/${contractorId}/projects/${projectId}/units/${unitId}`);
  await setDoc(unitRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Get a single unit
 */
export async function getUnit(
  contractorId: string,
  projectId: string,
  unitId: string
) {
  const unitRef = doc(db, `contractors/${contractorId}/projects/${projectId}/units/${unitId}`);
  return getDoc(unitRef);
}

/**
 * Get all units for a project
 */
export async function getUnits(
  contractorId: string,
  projectId: string,
  constraints: QueryConstraint[] = []
) {
  const q = query(
    collection(db, `contractors/${contractorId}/projects/${projectId}/units`),
    orderBy('sortOrder', 'asc'),
    ...constraints
  );
  return getDocs(q);
}

/**
 * Get units for a specific segment
 */
export async function getUnitsBySegment(
  contractorId: string,
  projectId: string,
  segmentId: string,
  constraints: QueryConstraint[] = []
) {
  const q = query(
    collection(db, `contractors/${contractorId}/projects/${projectId}/units`),
    where('segmentId', '==', segmentId),
    orderBy('sortOrder', 'asc'),
    ...constraints
  );
  return getDocs(q);
}

/**
 * Delete a unit
 */
export async function deleteUnit(
  contractorId: string,
  projectId: string,
  unitId: string
): Promise<void> {
  const unitRef = doc(db, `contractors/${contractorId}/projects/${projectId}/units/${unitId}`);
  await deleteDoc(unitRef);
}

/**
 * Update unit's job stats
 */
export async function updateUnitJobStats(
  contractorId: string,
  projectId: string,
  unitId: string,
  stats: { jobCount: number; readyJobs: number; blockedJobs: number }
): Promise<void> {
  const unitRef = doc(db, `contractors/${contractorId}/projects/${projectId}/units/${unitId}`);
  await setDoc(unitRef, {
    ...stats,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

// ============================================
// JOB-PROJECT LINKAGE
// ============================================

/**
 * Link a job to a project and optionally a unit
 * This updates the job document with project linkage fields
 */
export async function linkJobToProject(
  contractorId: string,
  jobId: string,
  projectId: string,
  options: {
    unitId?: string;
    jobScopeType: JobScopeType;
    templateJobId?: string;
    isTemplate?: boolean;
  }
): Promise<void> {
  // Import dualWriteJob from main helpers to maintain dual-write pattern
  const { dualWriteJob } = await import('./firestore-helpers');

  await dualWriteJob(contractorId, jobId, {
    projectId,
    unitId: options.unitId,
    jobScopeType: options.jobScopeType,
    templateJobId: options.templateJobId,
    isTemplate: options.isTemplate,
  });
}

/**
 * Get all jobs for a project
 */
export async function getProjectJobs(
  contractorId: string,
  projectId: string,
  constraints: QueryConstraint[] = []
) {
  // Import from main helpers
  const { READ_MODE, ENABLE_DUAL_WRITE } = await import('./firestore-helpers');

  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/jobs`),
      where('projectId', '==', projectId),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'jobs'),
      where('contractorId', '==', contractorId),
      where('projectId', '==', projectId),
      ...constraints
    );
    return getDocs(q);
  }
}

/**
 * Get jobs for a specific unit
 */
export async function getUnitJobs(
  contractorId: string,
  projectId: string,
  unitId: string,
  constraints: QueryConstraint[] = []
) {
  // Import from main helpers
  const { READ_MODE } = await import('./firestore-helpers');

  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/jobs`),
      where('projectId', '==', projectId),
      where('unitId', '==', unitId),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'jobs'),
      where('contractorId', '==', contractorId),
      where('projectId', '==', projectId),
      where('unitId', '==', unitId),
      ...constraints
    );
    return getDocs(q);
  }
}

/**
 * Get shared/common jobs for a project (jobs without a specific unit)
 */
export async function getSharedProjectJobs(
  contractorId: string,
  projectId: string,
  constraints: QueryConstraint[] = []
) {
  // Import from main helpers
  const { READ_MODE } = await import('./firestore-helpers');

  if (READ_MODE === 'new') {
    const q = query(
      collection(db, `contractors/${contractorId}/jobs`),
      where('projectId', '==', projectId),
      where('jobScopeType', 'in', ['shared_common', 'campus_site']),
      ...constraints
    );
    return getDocs(q);
  } else {
    const q = query(
      collection(db, 'jobs'),
      where('contractorId', '==', contractorId),
      where('projectId', '==', projectId),
      where('jobScopeType', 'in', ['shared_common', 'campus_site']),
      ...constraints
    );
    return getDocs(q);
  }
}

// ============================================
// STATS COMPUTATION
// ============================================

/**
 * Recompute and update project stats based on units and jobs
 */
export async function recomputeProjectStats(
  contractorId: string,
  projectId: string
): Promise<{ unitStats: UnitStats; sharedPermitStats: SharedPermitStats }> {
  // Get all units
  const unitsSnapshot = await getUnits(contractorId, projectId);

  const unitStats: UnitStats = {
    total: unitsSnapshot.size,
    ready: 0,
    blocked: 0,
    submitted: 0,
    issued: 0,
  };

  // Count units by status
  unitsSnapshot.forEach((doc) => {
    const unit = doc.data() as Unit;
    // Here we'd look at the unit's job statuses
    // For now, just count based on unit status
    switch (unit.status) {
      case 'permitting':
        unitStats.ready++;
        break;
      case 'construction':
        unitStats.submitted++;
        break;
      case 'complete':
        unitStats.issued++;
        break;
    }
  });

  // Get shared/common jobs
  const sharedJobsSnapshot = await getSharedProjectJobs(contractorId, projectId);

  const sharedPermitStats: SharedPermitStats = {
    total: sharedJobsSnapshot.size,
    ready: 0,
    blocked: 0,
    submitted: 0,
    issued: 0,
  };

  // Count shared permits by compliance status
  sharedJobsSnapshot.forEach((doc) => {
    const job = doc.data();
    const complianceStatus = job.complianceStatus || job.compliance?.status;
    const inspectionStatus = job.inspectionStatus;

    if (inspectionStatus === 'passed') {
      sharedPermitStats.issued++;
    } else if (job.submittedForPermitsAt) {
      sharedPermitStats.submitted++;
    } else if (complianceStatus === 'pass') {
      sharedPermitStats.ready++;
    } else if (complianceStatus === 'fail') {
      sharedPermitStats.blocked++;
    }
  });

  // Update project with computed stats
  await updateProjectStats(contractorId, projectId, unitStats, sharedPermitStats);

  return { unitStats, sharedPermitStats };
}

/**
 * Compute project status based on unit/job progress
 */
export function computeProjectStatus(
  unitStats: UnitStats,
  sharedPermitStats: SharedPermitStats
): DevelopmentProjectStatus {
  const totalUnits = unitStats.total;
  const totalShared = sharedPermitStats.total;

  if (totalUnits === 0 && totalShared === 0) {
    return 'planning';
  }

  if (unitStats.issued === totalUnits && sharedPermitStats.issued === totalShared) {
    return 'complete';
  }

  if (unitStats.issued > 0 || sharedPermitStats.issued > 0) {
    return 'construction';
  }

  if (unitStats.submitted > 0 || sharedPermitStats.submitted > 0) {
    return 'permitting';
  }

  return 'planning';
}
